import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, Switch, Button, Alert, CircularProgress,
    InputAdornment, List, ListItem, ListItemAvatar, ListItemText,
    ListItemSecondaryAction, Chip, Divider, Tooltip,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { buildConditionsJson, parseConditionsJson } from "../rules/ConditionBuilder";
import { segmentErrorMessage } from "./segmentErrorMessage";
import { searchLoyaltyCustomersPaginated } from "../../utils/customerLookup";
import SegmentConditionsEditor from "./SegmentConditionsEditor";
import {
    usesLoyaltyConditionBuilder,
    formatUnomiConditionJson,
    parseUnomiConditionJson,
} from "./segmentConditionsUtils";

// ─── helpers ──────────────────────────────────────────────────────────────────

const avatarLetter = (pid) => (pid ? pid.charAt(0).toUpperCase() : "?");
const avatarColor = (pid) => {
    if (!pid) return "#94a3b8";
    const hue = Math.abs(pid.split("").reduce((acc, c) => acc * 31 + c.charCodeAt(0), 0)) % 360;
    return `hsl(${hue}, 55%, 48%)`;
};
const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("fr-FR", { dateStyle: "short" }) : "–";

// ─── Inline live member manager (API-backed, for Edit dialog) ─────────────────

const InlineMemberManager = ({ segment, brandName }) => {
    const [members, setMembers] = useState([]);
    const [profiles, setProfiles] = useState({});
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [membersError, setMembersError] = useState(null);

    // search state
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [adding, setAdding] = useState({});
    const [removing, setRemoving] = useState({});
    const debounceRef = useRef(null);

    const existingIds = new Set(members.map((m) => m.customer_id));

    // ── Load existing members ──────────────────────────────────────────────
    const loadMembers = useCallback(async () => {
        if (!segment?.id || !brandName) return;
        setLoadingMembers(true);
        setMembersError(null);
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members`,
                { headers: { "X-Brand": brandName } },
            );
            if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
            const data = await res.json();
            const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
            setMembers(items);
        } catch (err) {
            setMembersError(err.message);
        } finally {
            setLoadingMembers(false);
        }
    }, [segment?.id, brandName]);

    useEffect(() => {
        setMembers([]);
        setProfiles({});
        setQuery("");
        setResults([]);
        loadMembers();
    }, [loadMembers]);

    // ── Customer search ────────────────────────────────────────────────────
    const search = useCallback(async (q) => {
        if (!q.trim()) { setResults([]); return; }
        setSearching(true);
        setSearchError(null);
        try {
            const data = await searchLoyaltyCustomersPaginated(brandName, q, { limit: 20, offset: 0 });
            setResults(Array.isArray(data.items) ? data.items : []);
        } catch (err) {
            setSearchError(err.message);
        } finally {
            setSearching(false);
        }
    }, [brandName]);

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(val), 380);
    };

    // ── Add member ─────────────────────────────────────────────────────────
    const handleAdd = async (customer) => {
        setAdding((p) => ({ ...p, [customer.id]: true }));
        setSearchError(null);
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ customer_id: customer.id }),
                },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Ajout membre"));
            }
            const member = await res.json();
            setMembers((prev) => {
                if (prev.some((m) => m.customer_id === member.customer_id)) return prev;
                return [member, ...prev];
            });
            setProfiles((p) => ({ ...p, [customer.id]: customer }));
        } catch (err) {
            setSearchError(err.message);
        } finally {
            setAdding((p) => ({ ...p, [customer.id]: false }));
        }
    };

    // ── Remove member ──────────────────────────────────────────────────────
    const handleRemove = async (customerId) => {
        setRemoving((p) => ({ ...p, [customerId]: true }));
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members/${customerId}`,
                { method: "DELETE", headers: { "X-Brand": brandName } },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Retrait membre"));
            }
            setMembers((prev) => prev.filter((m) => m.customer_id !== customerId));
        } catch {
            /* keep UI stable */
        } finally {
            setRemoving((p) => ({ ...p, [customerId]: false }));
        }
    };

    return (
        <Box px={1.5} py={1.5} sx={{
            borderRadius: 2,
            border: `1px solid ${SECTION_COLORS.segments.main}25`,
            bgcolor: `${SECTION_COLORS.segments.main}05`,
        }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                <PeopleAltIcon sx={{ fontSize: 16, color: SECTION_COLORS.segments.main }} />
                <Typography fontSize={13} fontWeight={700} color="#334155">
                    Membres du segment
                </Typography>
                <Chip
                    label={loadingMembers ? "…" : `${members.length} membre${members.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                        ml: "auto", fontSize: 10, height: 20,
                        bgcolor: `${SECTION_COLORS.segments.main}15`,
                        color: SECTION_COLORS.segments.main, fontWeight: 700,
                    }}
                />
            </Box>

            {membersError && (
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>{membersError}</Alert>
            )}

            {/* Search box */}
            <TextField
                fullWidth size="small"
                placeholder="Rechercher un client par profil ID…"
                value={query}
                onChange={handleQueryChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                    endAdornment: searching ? (
                        <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                    ) : null,
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
            />

            {searchError && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>{searchError}</Alert>
            )}

            {/* Search results dropdown */}
            {results.length > 0 && (
                <List dense disablePadding sx={{
                    mt: 1, maxHeight: 190, overflowY: "auto",
                    border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
                }}>
                    {results.map((c, idx) => {
                        const alreadyIn = existingIds.has(c.id);
                        const isAdding = adding[c.id];
                        return (
                            <React.Fragment key={c.id}>
                                {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                    <ListItemAvatar sx={{ minWidth: 36 }}>
                                        <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(c.profile_id) }}>
                                            {avatarLetter(c.profile_id)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{c.profile_id}</Typography>}
                                        secondary={<Typography fontSize={10} color="text.secondary">{c.loyalty_status_name || c.loyalty_status || "–"} · {c.status_points ?? 0} pts</Typography>}
                                        sx={{ my: 0 }}
                                    />
                                    <ListItemSecondaryAction>
                                        {alreadyIn ? (
                                            <Chip label="Déjà membre" size="small"
                                                icon={<CheckCircleIcon sx={{ fontSize: "12px !important" }} />}
                                                sx={{ fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a", height: 22 }} />
                                        ) : (
                                            <Tooltip title="Ajouter au segment">
                                                <span>
                                                    <IconButton size="small" disabled={isAdding} onClick={() => handleAdd(c)}
                                                        sx={{
                                                            color: SECTION_COLORS.segments.main,
                                                            bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                            width: 26, height: 26,
                                                            "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                        }}>
                                                        {isAdding
                                                            ? <CircularProgress size={14} color="inherit" />
                                                            : <PersonAddIcon sx={{ fontSize: 14 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}

            {!searching && query.trim() && results.length === 0 && (
                <Typography fontSize={12} color="text.secondary" mt={1} textAlign="center">
                    Aucun client trouvé pour « {query} »
                </Typography>
            )}

            {/* Current members */}
            <Box mt={2}>
                <Typography fontSize={11} fontWeight={600} color="#64748b" mb={0.75}>
                    Membres actuels
                </Typography>

                {loadingMembers ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={22} />
                    </Box>
                ) : members.length === 0 ? (
                    <Box py={2} display="flex" alignItems="center" justifyContent="center"
                        sx={{ border: "1px dashed #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
                        <Typography fontSize={11} color="text.secondary">
                            Aucun membre — utilisez la recherche ci-dessus pour en ajouter
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{
                        maxHeight: 200, overflowY: "auto",
                        border: "1px solid #e8ecf1", borderRadius: 2, bgcolor: "#fff",
                    }}>
                        {members.map((m, idx) => {
                            const prof = profiles[m.customer_id];
                            const pid = prof?.profile_id || m.customer_id;
                            const isRemoving = removing[m.customer_id];
                            return (
                                <React.Fragment key={m.customer_id}>
                                    {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                    <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                        <ListItemAvatar sx={{ minWidth: 36 }}>
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(pid) }}>
                                                {avatarLetter(pid)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>{pid}</Typography>}
                                            secondary={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Typography fontSize={10} color="text.secondary">
                                                        Ajouté le {fmtDate(m.created_at)}
                                                    </Typography>
                                                    <Chip label={m.source} size="small" sx={{
                                                        fontSize: 9, height: 16,
                                                        bgcolor: m.source === "STATIC" ? "#eff6ff" : "#f0fdf4",
                                                        color: m.source === "STATIC" ? "#1d4ed8" : "#15803d",
                                                    }} />
                                                </Box>
                                            }
                                            sx={{ my: 0 }}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Retirer du segment">
                                                <span>
                                                    <IconButton size="small" disabled={isRemoving} onClick={() => handleRemove(m.customer_id)}
                                                        sx={{ color: "#DC2626", bgcolor: "#fef2f2", width: 26, height: 26, "&:hover": { bgcolor: "#fee2e2" } }}>
                                                        {isRemoving
                                                            ? <CircularProgress size={14} color="inherit" />
                                                            : <PersonRemoveIcon sx={{ fontSize: 14 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Box>
    );
};

// ─── EditSegmentDialog ────────────────────────────────────────────────────────

const EditSegmentDialog = ({ open, segment, onClose, onSaved }) => {
    const [form, setForm] = useState({ name: "", description: "", is_dynamic: true, active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [combinator, setCombinator] = useState("and");
    const [condRows, setCondRows] = useState([]);
    const [unomiConditionJson, setUnomiConditionJson] = useState("");

    useEffect(() => {
        if (!segment) return;
        setForm({
            name: segment.name || "",
            description: segment.description || "",
            is_dynamic: segment.is_dynamic ?? true,
            active: segment.active ?? true,
        });
        const parsed = parseConditionsJson(segment.conditions);
        setCombinator(parsed.combinator);
        setCondRows(parsed.rows);
        setUnomiConditionJson(formatUnomiConditionJson(segment.unomi_condition));
        setError(null);
    }, [segment]);

    const handleConditionsChange = (newCombinator, newRows) => {
        setCombinator(newCombinator);
        setCondRows(newRows);
    };

    const handleSubmit = async () => {
        if (!segment) return;
        setSubmitting(true);
        setError(null);
        try {
            const loyaltyBuilder = usesLoyaltyConditionBuilder(segment);
            let body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                is_dynamic: form.is_dynamic,
                active: form.active,
            };

            if (form.is_dynamic) {
                if (loyaltyBuilder) {
                    const conditions = buildConditionsJson(combinator, condRows);
                    if (!conditions) {
                        throw new Error("Un segment dynamique doit contenir au moins une condition.");
                    }
                    body = { ...body, conditions };
                } else {
                    let unomi_condition;
                    try {
                        unomi_condition = parseUnomiConditionJson(unomiConditionJson);
                    } catch {
                        throw new Error("Condition Unomi invalide : JSON attendu.");
                    }
                    if (!unomi_condition) {
                        throw new Error("Condition Unomi requise pour ce segment CDP.");
                    }
                    body = { ...body, unomi_condition };
                }
            } else {
                body = { ...body, conditions: null };
            }

            const recomputeQuery = form.is_dynamic ? "?recompute=true" : "";
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}${recomputeQuery}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", "X-Brand": segment.brand },
                    body: JSON.stringify(body),
                },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(segmentErrorMessage(res.status, e?.detail, "Modification segment"));
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!segment) return null;

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const loyaltyBuilder = segment ? usesLoyaltyConditionBuilder(segment) : true;
    const isValid = form.name.trim() && (
        !form.is_dynamic
        || (loyaltyBuilder ? condRows.length > 0 : unomiConditionJson.trim().length > 0)
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Segment
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{segment.brand}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    {/* Name */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom du segment" value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            size="small" sx={fieldSx} />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>

                    {/* Dynamic toggle */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Segment dynamique</Typography>
                                <Typography fontSize={11} color="text.secondary">
                                    {form.is_dynamic
                                        ? "Actif : membres calculés automatiquement à partir des conditions"
                                        : "Inactif : vous gérez les membres manuellement ci-dessous"}
                                </Typography>
                            </Box>
                            <Switch
                                checked={form.is_dynamic}
                                onChange={() => setForm((p) => ({ ...p, is_dynamic: !p.is_dynamic }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.segments.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.segments.main },
                                }} />
                        </Box>
                    </Grid>

                    {/* ── Dynamic: condition builder ── */}
                    {form.is_dynamic && (
                        <Grid item xs={12}>
                            <Typography fontSize={13} fontWeight={700} mb={1} color="#334155">
                                Conditions du segment
                            </Typography>
                            <Typography fontSize={11} color="text.secondary" mb={1}>
                                Les membres des segments dynamiques sont recalculés par le moteur fidélité
                                (POST …/recompute), pas par le CDP.
                            </Typography>
                            <SegmentConditionsEditor
                                segment={segment}
                                brandName={segment.brand}
                                combinator={combinator}
                                condRows={condRows}
                                onConditionsChange={handleConditionsChange}
                                unomiJson={unomiConditionJson}
                                onUnomiJsonChange={setUnomiConditionJson}
                            />
                        </Grid>
                    )}

                    {/* ── Static: live member manager ── */}
                    {!form.is_dynamic && (
                        <Grid item xs={12}>
                            <InlineMemberManager segment={segment} brandName={segment.brand} />
                        </Grid>
                    )}

                    {/* Active toggle */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer le segment</Typography>
                                <Typography fontSize={11} color="text.secondary">
                                    Disponible pour les règles et jobs internes
                                </Typography>
                            </Box>
                            <Switch
                                checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.segments.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.segments.main },
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.segments.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement…" : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSegmentDialog;
